import {Component} from 'panel';
import type {VNode} from 'snabbdom';

import {ButtonType} from 'common/components/button/constants';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {formatText} from 'common/util/format-text';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {promisifyModal} from '../modal/utils';
import {ConfirmAttrs, ConfirmProps, THEME, ValidatorFn} from './types';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import css from './index.inline.styl';

interface ConfirmState {
  inputValue?: string;
  inputError: string | null;
}

interface ConfirmJsxProps {
  attrs?: ConfirmAttrs;
  on?: {
    change?: (ev: ChangeEvent) => void;
    submit?: (ev: SubmitEvent) => void;
  };
}

export type ChangeEvent = CustomEvent<{state: string}>;

export type SubmitEvent = CustomEvent<{action: `confirm`; inputValue: string} | {action: `cancel`}>;

export class Confirm extends Component<ConfirmState> {
  inputElm: HTMLInputElement;
  inputValidator: ValidatorFn;
  bannerContent: VNode;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'hide-cancel-button': `boolean`,
      'cancel-button-label': {type: `string`, default: `Cancel`},
      closeable: `boolean`,
      'not-fullscreen': `boolean`,
      'confirm-button-disabled': `boolean`,
      'grow-to-content': `boolean`,
      'confirm-button-label': {type: `string`},
      open: `boolean`,
      title: `string`,
      description: `string`,
      'description-allow-newline': {
        type: `boolean`,
        description: `Allows escape new lines and line breaks to be rendered in new lines.`,
      },
      theme: {
        type: `string`,
        default: THEME.CONFIRM,
        enum: Object.values(THEME),
      },
      'show-input': {
        type: `boolean`,
        description: `Presents a required input field to the user. The result is provided in the confirm event: details.inputValue.`,
      },
      'initial-input-value': {
        type: `string`,
        default: ``,
        description: `Provides an initial value for the input field.`,
      },
      'allow-empty-input-value': {
        type: `boolean`,
        description: `Allows the input field to be submitted as an empty value.`,
      },
      'banner-text': {
        type: `string`,
      },
      'remove-host-style': {
        type: `boolean`,
        description: `Prevent adding absolute host-style to modal`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        inputValue: ``,
        inputError: null,
      },

      helpers: {
        ButtonType,
        formatText,
        modalChanged: ({detail}: CustomEvent) => {
          this.dispatchEvent(new CustomEvent(`change`, {detail}));
          if (detail.state === `open` && this.inputElm) {
            requestAnimationFrame(() => this.inputElm.focus());
          }
        },

        cancel: () => {
          const ev = new CustomEvent(`submit`, {cancelable: true, detail: {action: `cancel`}});
          this.dispatchEvent(ev);
          if (!ev.defaultPrevented) {
            this.setAttribute(`open`, `false`);
          }
        },

        confirm: () => {
          this.confirm();
        },

        isConfirmButtonDisabled: (): boolean => {
          const isValidInputState = this.attr(`allow-empty-input-value`) || Boolean(this.state.inputValue);
          return this.attr(`confirm-button-disabled`) || (this.attr(`show-input`) && !isValidInputState);
        },

        // Input helpers

        changeInputValue: (ev) => {
          ev.stopPropagation();
          this.update({inputValue: ev.target.value});
        },

        commitInputValue: ({keyCode}: KeyboardEvent) => {
          if (keyCode === NAVIGATION_KEY_CODES.ENTER && !this.helpers.isConfirmButtonDisabled()) {
            this.helpers.confirm();
          }
        },

        onInputInserted: (vnode) => (this.inputElm = vnode.elm),
      },
    };
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case `initial-input-value`:
        this.update({inputValue: newValue});
        break;
      case `input-error-message`:
        this.update({inputError: newValue || null});
        break;
    }
  }

  confirm() {
    this.validate();

    if (!this.state.inputError) {
      const detail = {
        action: `confirm`,
        inputValue: this.state.inputValue,
      };
      const ev = new CustomEvent(`submit`, {cancelable: true, detail});
      this.dispatchEvent(ev);
      if (!ev.defaultPrevented) {
        this.setAttribute(`open`, `false`);
      }
    }
  }

  private validate() {
    if (!this.inputValidator) {
      return;
    }

    this.update({inputError: this.inputValidator(this.state.inputValue)});
  }
}

// `change` and `submit` are HTML native events, snabbdom module does not like us to re-patch them as custom events
// we should move toward `params.onConfirm` `params.onChange` etc. to avoid overriding native HTML events
export const ConfirmEl = defineAndMakeJsxFactory<Omit<ConfirmJsxProps, 'on'>>(`mp-confirm`, Confirm);
export const ConfirmReact = panel2react<ConfirmJsxProps>(`mp-confirm`, Confirm);

export async function confirm(attrs: ConfirmAttrs): Promise<boolean> {
  const closeEv = await promisifyModal({tagName: `mp-confirm`, attrs, closeEvents: [`submit`]});
  return closeEv.detail.action === `confirm`;
}

export function confirmDelete(attrs: ConfirmAttrs): Promise<boolean> {
  return confirm({...attrs, theme: THEME.DELETE});
}

export async function prompt(
  attrs: ConfirmAttrs,
  props: ConfirmProps = {},
): Promise<{didConfirm: boolean; inputValue: string}> {
  const closeEv = await promisifyModal({
    tagName: `mp-confirm`,
    attrs: {
      closeable: true,
      'confirm-button-label': `Save`,
      ...attrs,
      elref: `confirmPrompt`,
    },
    props,
    closeEvents: [`submit`],
  });

  return {
    didConfirm: closeEv.type === `submit` && closeEv.detail.action === `confirm`,
    inputValue: closeEv.type === `submit` ? closeEv.detail.inputValue : null,
  };
}
